import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const defaultReceiptConfig = {
  template: 'RCPT-{YYYY}{MM}-{SEQ}',
  seq_padding: 5,
  reset_cycle: 'NEVER',
  is_active: true,
  is_configured: false,
  current_seq: 0,
  last_reset_key: null
};

/**
 * Guest Receipts (docs/DEV_PMS_OPERATIONS_ENHANCEMENT.md Module 4) — receipt
 * numbering config. Mirrors the Invoice Number Configuration section in
 * PmsBillingInfoMaster.js exactly, as an isolated screen so that file's
 * existing state management isn't touched. Receipt numbers are stamped on
 * exactly 3 flows: advance, refund (Reservation > Advance modal), and
 * service pay-now (Front Office > Guest Requests) — never on charges.
 */
function PmsReceiptSettings({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [config, setConfig] = useState(defaultReceiptConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const load = async () => {
    if (!selectedProperty?.id) { setConfig(defaultReceiptConfig); return; }
    try {
      const res = await fetch('/api/v1/pms/receipt-number-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load receipt number config');
      setConfig({
        template: data?.template || defaultReceiptConfig.template,
        seq_padding: Number(data?.seq_padding || defaultReceiptConfig.seq_padding),
        reset_cycle: data?.reset_cycle || defaultReceiptConfig.reset_cycle,
        is_active: data?.is_active !== false,
        is_configured: data?.is_configured === true,
        current_seq: Number(data?.current_seq || 0),
        last_reset_key: data?.last_reset_key || null
      });
    } catch (err) {
      setError(err?.message || 'Failed to load receipt number config');
    }
  };

  useEffect(() => {
    setError(''); setMessage(''); setConfig(defaultReceiptConfig);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    const tpl = String(config.template || '').trim();
    if (!tpl) return setError('Template is required');
    if (!tpl.toUpperCase().includes('{SEQ}')) return setError('Template must include {SEQ} token');
    setSaving(true); setError(''); setMessage('');
    try {
      const payload = {
        template: tpl,
        seq_padding: Number(config.seq_padding || 0),
        reset_cycle: String(config.reset_cycle || 'NEVER').toUpperCase(),
        is_active: config.is_active !== false
      };
      const res = await fetch('/api/v1/pms/receipt-number-config', {
        method: 'PUT', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save receipt number config');
      setMessage('Receipt number config saved');
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to save receipt number config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Receipt Number Configuration</h3>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', marginTop: '-0.4rem' }}>
        Applies to advance receipts, refund receipts, and guest-service pay-now receipts. Charges never get a receipt number.
      </p>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div className="filter-bar" style={{ marginBottom: '0.8rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 1fr', gap: '0.55rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Template
            <input
              value={config.template}
              onChange={(e) => setConfig((p) => ({ ...p, template: e.target.value }))}
              placeholder="RCPT-{HOTELCODE}-{YYYY}{MM}-{SEQ}"
            />
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>
              Tokens: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{DD}'} {'{FY}'} {'{SEQ}'} {'{HOTELCODE}'} &mdash; {'{SEQ}'} is required.
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Seq Padding
            <input
              type="number" min={1} max={12}
              value={config.seq_padding}
              onChange={(e) => setConfig((p) => ({ ...p, seq_padding: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Reset Cycle
            <select value={config.reset_cycle} onChange={(e) => setConfig((p) => ({ ...p, reset_cycle: e.target.value }))}>
              <option value="NEVER">NEVER</option>
              <option value="YEARLY">YEARLY (Jan-Dec)</option>
              <option value="FINANCIAL_YEAR">FINANCIAL YEAR (Apr-Mar)</option>
              <option value="MONTHLY">MONTHLY</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Status
            <select value={config.is_active ? 'Y' : 'N'} onChange={(e) => setConfig((p) => ({ ...p, is_active: e.target.value === 'Y' }))}>
              <option value="Y">Active</option>
              <option value="N">Inactive</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.82rem', color: '#4a5568' }}>
            Current Sequence: <b>{Number(config.current_seq || 0)}</b>
            {config.last_reset_key ? <span> | Last Reset Key: <b>{config.last_reset_key}</b></span> : null}
          </div>
          <button onClick={save} disabled={saving} style={{ minWidth: '170px' }}>
            {saving ? 'Saving...' : 'Save Receipt Config'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PmsReceiptSettings;
